import { useEffect, useState } from "react";
import { api } from "./api.js";
import { logError } from "./logger.js";

// Virtual HR ("Sirra"): the assistant that checks uploaded documents. Its name, and where the
// Super HR Admin has switched it off, come from the server once and are shared by every component.

/** Where a document is uploaded: HR's employee profile, or the new-hire onboarding wizard. */
export type SirraContext = "employee" | "onboarding";

export interface VirtualHrStatus {
  /** Master switch. */
  enabled: boolean;
  /** Each only counts while the master switch is on. */
  employeeEnabled: boolean;
  onboardingEnabled: boolean;
  assistantName: string;
  disabledDocTypes: string[];
}

const DEFAULT_STATUS: VirtualHrStatus = { enabled: true, employeeEnabled: true, onboardingEnabled: true, assistantName: "Sirra Virtual HR", disabledDocTypes: [] };

let current: VirtualHrStatus = DEFAULT_STATUS;
let loaded = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: VirtualHrStatus) {
  current = next;
  loaded = true;
  listeners.forEach((fn) => fn());
}

/** Whatever the server sent, made safe to use: a missing or malformed field means "on" / the default. */
function normalize(raw: unknown): VirtualHrStatus {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<VirtualHrStatus>;
  return {
    enabled: r.enabled !== false,
    employeeEnabled: r.employeeEnabled !== false,
    onboardingEnabled: r.onboardingEnabled !== false,
    assistantName: typeof r.assistantName === "string" && r.assistantName.trim() ? r.assistantName : DEFAULT_STATUS.assistantName,
    disabledDocTypes: Array.isArray(r.disabledDocTypes) ? r.disabledDocTypes : [],
  };
}

/** Re-reads the status, e.g. after the admin page saves a change. */
export function refreshVirtualHr(): Promise<void> {
  inflight = api.get<VirtualHrStatus>("/virtual-hr/status")
    .then(({ data }) => publish(normalize(data)))
    .catch((err) => logError("virtualHr.status", err))
    .finally(() => { inflight = null; });
  return inflight;
}

export const firstName = (name: string): string => name.trim().split(/\s+/)[0] || "Sirra";

export function useVirtualHr() {
  const [status, setStatus] = useState(current);
  useEffect(() => {
    const fn = () => setStatus(current);
    listeners.add(fn);
    if (!loaded && !inflight) void refreshVirtualHr();
    return () => { listeners.delete(fn); };
  }, []);
  // The master switch AND this place's own switch. Off means plain uploads there: no check, no
  // details read, no autofill, and in onboarding no Sirra card or voice.
  const contextEnabled = (context: SirraContext): boolean =>
    status.enabled && (context === "onboarding" ? status.onboardingEnabled : status.employeeEnabled) !== false;
  return {
    ...status,
    /** "Sirra" from "Sirra Virtual HR", for short labels. */
    shortName: firstName(status.assistantName),
    contextEnabled,
    /** False when the admin switched Virtual HR off globally, for this place, or for this document. */
    isDocEnabled: (docType: string, context: SirraContext = "employee") => contextEnabled(context) && !status.disabledDocTypes.includes(docType),
  };
}
